import SlideoverModal from "src/components/SlideoverModal";
import { RootState } from "src/stores";
import { connect } from "react-redux";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { queryQueueProps } from "src/interfaces";
import Lucide from "src/base-components/Lucide";
import LoadingIcon from "src/base-components/LoadingIcon";
import { dateParser, isAdmin, isManager } from "src/helpers";
import { fetchQueues, updateQueue } from "src/stores/queue";
import _ from "lodash";
import { clearAllListeners } from "@reduxjs/toolkit";
import Card from "src/components/Card";
import Button from "src/base-components/Button";
import { fetchAssignLocationsService } from "src/services";

// ─── Types ────────────────────────────────────────────────────────────────────
interface LocationProps {
  id: number;
  name: string;
}
// ─── Component props ──────────────────────────────────────────────────────────
interface ComponentProps {
  visible: boolean;
  setVisible: (payload: boolean) => void;
  // fetchData: () => void;
  fetchData: (payload: { location_ids: string[] }) => void;
  onUpdate: (payload: {
    id: string;
    query_id: string;
    user_id: number;
    callback?: Function;
  }) => void;
  queryQueues: any | undefined;
  token: any;
}

interface Location {
  id: string;
  name: string;
  is_primary: boolean;
}

function Component(props: ComponentProps) {
  const { queryQueues, fetchData, setVisible, visible, onUpdate, token } =
    props;
  const navigate = useNavigate();
  const [loadingMore, setLoadingMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isLoadedAll, setIsLoadedAll] = useState(false);
  const isLoading = queryQueues?.loading

  const [primaryLocation, setPrimaryLocation] = useState<Location | null>(null);
  const [secondaryLocations, setSecondaryLocations] = useState<Location[]>([]);

  // ── Filter state ──────────────────────────────────────────────────────────
  // Both are INDEPENDENT checkboxes — checking secondary never affects primary
  const [isPrimaryActive, setIsPrimaryActive] = useState<boolean>(true);  // auto-checked by default
  const [isSecondaryActive, setIsSecondaryActive] = useState<boolean>(false);
  // Multi-select for secondary (only when secondaryLocations.length > 1)
  // const [selectedSecondary, setSelectedSecondary] = useState<Set<string>>(new Set());
  const [selectedSecondary, setSelectedSecondary] = useState<string[]>([]);

  const toggleSecondaryLocation = (id: string) => {
    setSelectedSecondary((prev) =>
      prev.includes(id)
        ? prev.filter((i) => i !== id)
        : [...prev, id]
    );
  };

  // ── Data loading ──────────────────────────────────────────────────────────
  // const loadData = () => {
  //   if (isManager() || isAdmin()) {
  //     fetchData();
  //   }
  // };

  const loadData = () => {
    setLoading(true);
    if (isManager() || isAdmin()) {
      const location_ids = getSelectedLocationIds();
      fetchData({
        location_ids,
      });
    }
    setLoading(false);
  };

  const getSelectedLocationIds = (): string[] => {
    const ids: string[] = [];

    if (isPrimaryActive && primaryLocation) {
      ids.push(primaryLocation.id);
    }

    if (isSecondaryActive) {
      if (secondaryLocations.length === 1) {
        ids.push(secondaryLocations[0].id);
      } else {
        ids.push(...Array.from(selectedSecondary));
      }
    }

    return ids;
  };

  const fetchLocations = async () => {
    setLoading(true);
    try {
      const res: any = await fetchAssignLocationsService();

      const data: Location[] = res;
      const primary = data.find((loc) => loc.is_primary);
      const secondary = data.filter((loc) => !loc.is_primary);

      setPrimaryLocation(primary || null);
      setSecondaryLocations(secondary);

      if (primary) {
        fetchData({
          location_ids: [primary.id],
        });
      }


    } catch (error) {
      setLoading(false);
      console.error("Error fetching locations", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {

    if (visible && isManager()) {
      fetchLocations();
      // loadData();
    }
    else if (visible && isAdmin()) {
      loadData();
    }
    else {
      resetState();
    }
  }, [visible]);

  const resetState = () => {
    setPrimaryLocation(null);
    setSecondaryLocations([]);

    setIsPrimaryActive(true);
    setIsSecondaryActive(false);
    setSelectedSecondary([]);

    setLoading(false);
    setLoadingMore(false);
    setIsLoadedAll(false);
  };

  useEffect(() => {

    if (visible) {
      loadData();
    }

    // addEventListener("focus", () => {
    //   if (props?.token && localStorage.getItem("deviceToken")) {
    //     loadData();
    //   }
    // });
    return () => {
      clearAllListeners();
    };
  }, [isPrimaryActive, selectedSecondary, isSecondaryActive, primaryLocation]);

  return (
    <>
      <SlideoverModal
        className="p-2"
        visible={visible}
        onClose={() => setVisible(false)}
        headerTitle="Queries waiting for billing/payment"
      >
        {loading ? (
          <div className="flex w-[100%] justify-center py-16">
            <LoadingIcon
              color="#64748b"
              icon="oval"
              className="w-8 h-8 mx-auto"
            />
          </div>
        ) : (
          <>
            {/* ── LOCATION FILTER ────────────────────────────────────────── */}
            {isManager() && <div className="border-b border-slate-100 bg-slate-50 px-3 py-2.5 flex justify-around items-center">

              {/* ── Primary location checkbox ─────────────────────────────── */}
              {primaryLocation && (
                <label className="flex items-center gap-2 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={isPrimaryActive}
                    onChange={() => setIsPrimaryActive((prev) => !prev)}
                    className="w-3.5 h-3.5 rounded border-slate-300 accent-[#075985]"
                  />
                  <Lucide icon="MapPin" className="w-3 h-3 text-slate-400" />
                  <span className="text-[14px] font-medium">
                    {primaryLocation.name}
                  </span>
                </label>
              )}

              {/* ── Secondary location checkbox ───────────────────────────── */}
              {secondaryLocations.length > 0 && (
                <label className="flex items-center gap-2 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={isSecondaryActive}
                    onChange={() => setIsSecondaryActive((prev) => {
                      const newValue = !prev;
                      if (!newValue) {
                        setSelectedSecondary([]);
                      }
                      else {
                        toggleSecondaryLocation(secondaryLocations[0]?.id);
                      }
                      return newValue;
                    })}
                    className="w-3.5 h-3.5 rounded border-slate-300 accent-[#075985]"
                  />
                  <Lucide icon="MapPin" className="w-3 h-3 text-slate-400" />
                  <span className="text-[14px] font-medium">
                    {secondaryLocations.length === 1
                      ? secondaryLocations[0].name
                      : "Secondary location"}
                  </span>
                </label>
              )}

            </div>}

            {isManager() && isSecondaryActive && secondaryLocations.length > 1 && (
              <div className="mt-2 ml-7 flex flex-wrap gap-1.5 border-l-2 border-slate-100 pl-3">
                {secondaryLocations.map((loc) => (
                  <label
                    key={loc.id}
                    className="flex items-center gap-2 cursor-pointer group"
                  >
                    <input
                      type="checkbox"
                      checked={selectedSecondary.includes(loc.id)}
                      onChange={() => toggleSecondaryLocation(loc.id)}
                      className="w-3 h-3 rounded border-slate-300 accent-[#075985] cursor-pointer"
                    />
                    <span
                      className={`text-[14px] transition-colors duration-100
                          ${selectedSecondary.includes(loc.id)
                          ? "text-[#075985] font-medium"
                          : "text-slate-500 group-hover:text-slate-700"}
                        `}
                    >
                      {loc.name}
                    </span>
                  </label>
                ))}

                {/* Clear link */}
                {/* {hasSecondaryFilter && (
                  <button
                    onClick={clearSecondary}
                    className="text-[10px] text-[#075985] font-medium hover:underline text-left mt-0.5"
                  >
                    Clear selection
                  </button>
                )} */}
              </div>
            )}
            {/* ── END FILTER ─────────────────────────────────────────────── */}

            {/* ── QUERY LIST ─────────────────────────────────────────────── */}
            {
              isLoading ? (
                <div className="flex w-[100%] justify-center py-16">
                  <LoadingIcon
                    color="#64748b"
                    icon="oval"
                    className="w-8 h-8 mx-auto"
                  />
                </div>
              )
                : (
                  <div>
                    {queryQueues?.list?.length === 0 ? (
                      <div className="flex w-[100%] justify-center py-28">No Queries</div>
                    ) : (

                      <div
                        onScroll={(event) => {
                          const { scrollTop, scrollHeight, clientHeight } =
                            event?.currentTarget;
                          const distanceFromBottom =
                            scrollHeight - scrollTop - clientHeight;
                          if (distanceFromBottom == 0 && !isLoadedAll) {
                            if (!loadingMore && !loading) {
                              setLoadingMore(true);
                              // loadData(true);
                            }
                          } else if (distanceFromBottom > 0) {
                            setIsLoadedAll(false);
                          }
                        }}
                        className="flex flex-col gap-0.5 mt-[10px]"
                      ></div>
                    )}
                    <div className="flex flex-col gap-2">
                      {!_.isEmpty(queryQueues?.list) &&
                        queryQueues?.list?.map((item: any, itemKey) => (
                          <Card key={itemKey}>
                            <div>
                              <div className="flex justify-between">
                                <div className="flex items-center text-[#075985] ">
                                  {item?.query_no}
                                </div>
                                <div className="text-[12px] text-right">
                                  {dateParser(item.updated_at, "DD-MM-YYYY")}
                                </div>
                              </div>
                              <div className="flex justify-between">
                                <div className="flex flex-col my-2">
                                  <div className="text-[12px]">
                                    {item?.queries?.customer?.fullname}
                                  </div>
                                  <div
                                    className="text-[10px]"
                                    style={{ lineHeight: "1rem" }}
                                  >
                                    Customer ID:{" "}
                                    {item?.queries?.customer?.customer_id || ""}
                                  </div>
                                </div>
                                <div className="flex flex-col leading-4 my-1">
                                  <div className="text-[10px] font-medium text-[#075985] text-end ">
                                    Payment/Billing Amount:
                                  </div>
                                  <div className="text-[12px] text-left">
                                    ${item?.payment_amount}
                                  </div>
                                </div>
                              </div>

                              <div className="flex flex-row justify-between mb-1">
                                <div className="flex flex-col leading-4 my-1 w-full">
                                  <div className="text-[10px] font-medium text-[#075985] text-start ">
                                    Assigned by:
                                  </div>
                                  <div className="text-[12px] text-left">
                                    {item?.assigned_by_user?.name}
                                  </div>
                                </div>
                                <Button
                                  variant="outline-primary"
                                  className=" font-bold w-32"
                                  onClick={() => {
                                    if (item?.status == 0) {
                                      onUpdate({
                                        id: item?.id,
                                        query_id: item?.query_id,
                                        user_id: token?.id,
                                      });
                                    }
                                    props?.setVisible(false);
                                    if (
                                      location?.pathname !=
                                      `/query-detail/${item?.query_id}` ||
                                      location?.pathname !=
                                      `/amadeus-query-detail/${item?.query_id}`
                                    ) {
                                      if (item.queries.query_category == "from_amadeus") {
                                        navigate(`/amadeus-query-detail/${item?.query_id}`);
                                      } else {
                                        navigate(`/query-detail/${item?.query_id}`);
                                      }
                                    }
                                  }}
                                >
                                  Proceed
                                  <Lucide icon="ArrowRight" className="w-4 h-4 ml-2" />
                                </Button>
                              </div>
                            </div>
                          </Card>
                        ))}
                    </div>
                  </div>
                )
            }
          </>
        )}
      </SlideoverModal>
    </>
  );
}

const mapStateToProps = ({ queries, authUser, queues }: RootState) => ({
  token: authUser,
  queryQueues: queues,
});

const mapDispatchToProps = (dispatch: any) => ({
  // fetchData: () => dispatch(fetchQueues()),
  fetchData: (payload?: { location_ids?: string[] }) => dispatch(fetchQueues(payload)),
  onUpdate: (payload: any) => dispatch(updateQueue(payload)),
});
export default connect(mapStateToProps, mapDispatchToProps)(Component);
